import React, { useState, useEffect, useMemo } from 'react';
import { dataService } from '../services/dataService';
import { parseProject, parseTask, projectProgress, dueState } from '../shared/projectlyContract';

const RANGES = [
  { label: 'Last 7 days',  days: 7  },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
];

const STATUS_COLORS = { completed: '#10b981', failed: '#f43f5e', paused: '#f59e0b' };
const TRIGGER_COLORS = ['#6366f1', '#0ea5e9', '#f59e0b', '#10b981', '#f97316', '#a855f7', '#ec4899'];

// Resolve a run's moment in time — startedAt is an ISO string written by the
// engine; fall back to completedAt or the Firestore server timestamp.
function runDate(run) {
  if (run.startedAt) return new Date(run.startedAt);
  if (run.completedAt) return new Date(run.completedAt);
  if (run.timestamp?.toDate) return run.timestamp.toDate();
  return new Date(0);
}

function formatDuration(ms) {
  if (!ms) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

function relativeTime(d) {
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function HBar({ label, value, max, color, sub }) {
  const pct = max > 0 ? Math.min(Math.round((value / max) * 100), 100) : 0;
  return (
    <div className="hbar-row">
      <div className="hbar-label-wrap">
        <span className="hbar-label">{label}</span>
        {sub && <span className="hbar-sub">{sub}</span>}
      </div>
      <div className="hbar-track">
        <div className="hbar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="hbar-value">{value}</span>
    </div>
  );
}

// ── Projects & tasks report ──────────────────────────────────────────────────
// Point-in-time view over the SAME task documents both clients render:
// outstanding and overdue per project, and overdue load per assignee.
function ProjectReports({ workspaceId }) {
  const [projects, setProjects] = useState([]);
  const [taskMap, setTaskMap] = useState({});
  const [members, setMembers] = useState([]);

  useEffect(() => {
    if (!workspaceId) return;
    return dataService.subscribeProjects(list => setProjects(list.map(d => parseProject(d.id, d))));
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId || !projects.length) return;
    const unsubs = projects.map(p =>
      dataService.subscribeProjectTasks(p.id, tasks =>
        setTaskMap(prev => ({ ...prev, [p.id]: tasks.map(t => parseTask(t.id, t)) }))
      )
    );
    return () => unsubs.forEach(u => u());
  }, [workspaceId, projects.map(p => p.id).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!workspaceId) return;
    dataService.getWorkspaceMembers(workspaceId)
      .then(list => setMembers(list.map(m => ({ id: m.uid, name: m.username || m.email?.split('@')[0] || 'Member' }))))
      .catch(() => setMembers([]));
  }, [workspaceId]);

  const rows = useMemo(() => projects.map(p => {
    const tasks = taskMap[p.id] || [];
    const { done, total, pct, doneStatus } = projectProgress({ statuses: p.statuses, tasks });
    const overdue = tasks.filter(t => dueState(t, doneStatus) === 'overdue').length;
    return { id: p.id, name: `${p.icon} ${p.name}`, open: total - done, done, total, pct, overdue, doneStatus, tasks };
  }), [projects, taskMap]);

  const openTotal = rows.reduce((s, r) => s + r.open, 0);
  const overdueTotal = rows.reduce((s, r) => s + r.overdue, 0);
  const doneTotal = rows.reduce((s, r) => s + r.done, 0);
  const allTotal = rows.reduce((s, r) => s + r.total, 0);

  const byAssignee = useMemo(() => {
    const counts = {};
    rows.forEach(r => r.tasks.forEach(t => {
      if (dueState(t, r.doneStatus) !== 'overdue') return;
      const key = t.assigneeId || '';
      counts[key] = (counts[key] || 0) + 1;
    }));
    return Object.entries(counts)
      .map(([id, count]) => ({
        name: id ? (members.find(m => m.id === id)?.name || 'Member') : 'Unassigned',
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [rows, members]);

  if (!projects.length) return null;

  const maxOpen = Math.max(1, ...rows.map(r => r.open));
  const maxAssignee = Math.max(1, ...byAssignee.map(a => a.count));

  const stats = [
    { label: 'Open tasks', value: String(openTotal), sub: `across ${rows.length} project${rows.length === 1 ? '' : 's'}`, icon: '☰', color: '#6366f1' },
    { label: 'Overdue', value: String(overdueTotal), sub: overdueTotal ? 'need attention' : 'all on time', icon: '⚠', color: '#f43f5e' },
    { label: 'Completed', value: allTotal ? `${Math.round((doneTotal / allTotal) * 100)}%` : '0%', sub: `${doneTotal} of ${allTotal} tasks`, icon: '✓', color: '#10b981' },
  ];

  return (
    <div data-testid="project-reports">
      <div className="stat-cards">
        {stats.map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-card-icon" style={{ background: s.color + '18', color: s.color }}>{s.icon}</div>
            <div className="stat-card-body">
              <div className="stat-card-value">{s.value}</div>
              <div className="stat-card-label">{s.label}</div>
              <div className="stat-card-sub">{s.sub}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="reports-grid">
        <div className="report-card">
          <div className="report-card-title">Open tasks by project</div>
          <div className="hbar-list">
            {rows.map(r => (
              <HBar key={r.id} label={r.name} value={r.open} max={maxOpen} color="#6366f1"
                sub={r.overdue ? `${r.overdue} overdue · ${r.pct}% done` : `${r.pct}% done`} />
            ))}
          </div>
        </div>
        <div className="report-card">
          <div className="report-card-title">Overdue by assignee</div>
          {byAssignee.length === 0
            ? <div className="stat-card-sub">Nothing overdue. 🎉</div>
            : (
              <div className="hbar-list">
                {byAssignee.map(a => (
                  <HBar key={a.name} label={a.name} value={a.count} max={maxAssignee} color="#f43f5e" />
                ))}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

export default function ReportsView({ workspaceId }) {
  const [range, setRange] = useState(RANGES[1]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workspaceId) { setLoading(false); return; }
    setLoading(true);
    const unsub = dataService.subscribePipelineLogs(workspaceId, (items) => {
      setLogs(items);
      setLoading(false);
    });
    return () => unsub && unsub();
  }, [workspaceId]);

  // ── Derive metrics from real runs within the selected window ────────────────
  const cutoff = Date.now() - range.days * 86400000;
  const runs = logs.filter(r => runDate(r).getTime() >= cutoff);

  const total = runs.length;
  const completed = runs.filter(r => r.status === 'completed').length;
  const failed = runs.filter(r => r.status === 'failed').length;
  const paused = runs.filter(r => r.status === 'paused').length;
  const successRate = total ? Math.round((completed / total) * 100) : 0;
  const avgMs = total ? runs.reduce((s, r) => s + (r.durationMs || 0), 0) / total : 0;

  const byStatus = [
    { status: 'Completed', count: completed, color: STATUS_COLORS.completed },
    { status: 'Failed',    count: failed,    color: STATUS_COLORS.failed },
    { status: 'Paused',    count: paused,    color: STATUS_COLORS.paused },
  ].filter(s => s.count > 0);

  const triggerGroups = {};
  runs.forEach(r => {
    const key = (r.triggerType || 'unknown').replace(/_/g, ' ');
    triggerGroups[key] = (triggerGroups[key] || 0) + 1;
  });
  const byTrigger = Object.entries(triggerGroups)
    .map(([name, count], i) => ({ name, count, color: TRIGGER_COLORS[i % TRIGGER_COLORS.length] }))
    .sort((a, b) => b.count - a.count);

  const workflowGroups = {};
  runs.forEach(r => {
    const key = r.triggerLabel || r.triggerNodeId || 'Workflow';
    if (!workflowGroups[key]) workflowGroups[key] = { runs: 0, failed: 0 };
    workflowGroups[key].runs += 1;
    if (r.status === 'failed') workflowGroups[key].failed += 1;
  });
  const byWorkflow = Object.entries(workflowGroups)
    .map(([name, v]) => ({ name, ...v }))
    .sort((a, b) => b.runs - a.runs)
    .slice(0, 8);

  const recent = [...runs]
    .sort((a, b) => runDate(b) - runDate(a))
    .slice(0, 8)
    .map(r => ({
      label: r.triggerLabel || 'Workflow',
      detail: `${r.status} · ${(r.steps || []).length} step${(r.steps || []).length === 1 ? '' : 's'}`,
      type: r.status === 'completed' ? 'success' : r.status === 'failed' ? 'warning' : 'info',
      time: relativeTime(runDate(r)),
    }));

  const maxTrigger = Math.max(1, ...byTrigger.map(t => t.count));
  const maxWorkflow = Math.max(1, ...byWorkflow.map(w => w.runs));
  const maxStatus = Math.max(1, ...byStatus.map(s => s.count));

  const stats = [
    { label: 'Total runs',   value: String(total),           sub: range.label.toLowerCase(),       icon: '▶', color: '#6366f1' },
    { label: 'Success rate', value: `${successRate}%`,        sub: `${completed} completed`,         icon: '✓', color: '#10b981' },
    { label: 'Failed runs',  value: String(failed),           sub: paused ? `${paused} paused` : 'in window', icon: '⚠', color: '#f43f5e' },
    { label: 'Avg duration', value: formatDuration(avgMs),    sub: 'per run',                        icon: '⏱', color: '#f59e0b' },
  ];

  return (
    <div className="reports-view">
      <div className="reports-header">
        <h2 className="reports-title">Reports</h2>
        <div className="range-tabs">
          {RANGES.map(r => (
            <button key={r.label} className={`range-tab ${range.label === r.label ? 'active' : ''}`} onClick={() => setRange(r)}>{r.label}</button>
          ))}
        </div>
      </div>

      <ProjectReports workspaceId={workspaceId} />

      {loading ? (
        <div className="reports-empty">Loading pipeline activity…</div>
      ) : total === 0 ? (
        <div className="reports-empty">
          <div className="reports-empty-icon">📊</div>
          <div className="reports-empty-title">No pipeline runs in this window</div>
          <div className="reports-empty-sub">
            Run a pipeline (or deploy a Lab) and your execution analytics will appear here automatically —
            success rates, durations, and activity per workspace.
          </div>
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="stat-cards">
            {stats.map(s => (
              <div key={s.label} className="stat-card">
                <div className="stat-card-icon" style={{ background: s.color + '18', color: s.color }}>{s.icon}</div>
                <div className="stat-card-body">
                  <div className="stat-card-value">{s.value}</div>
                  <div className="stat-card-label">{s.label}</div>
                  <div className="stat-card-sub">{s.sub}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="reports-grid">
            {/* Runs by status */}
            <div className="report-card">
              <div className="report-card-title">Runs by status</div>
              <div className="task-status-bars">
                {byStatus.map(s => (
                  <HBar key={s.status} label={s.status} value={s.count} max={maxStatus} color={s.color}
                    sub={`${Math.round((s.count / total) * 100)}%`} />
                ))}
              </div>
            </div>

            {/* Runs by trigger */}
            <div className="report-card">
              <div className="report-card-title">Runs by trigger type</div>
              <div className="hbar-list">
                {byTrigger.map(t => (
                  <HBar key={t.name} label={t.name} value={t.count} max={maxTrigger} color={t.color} />
                ))}
              </div>
            </div>

            {/* Runs by workflow */}
            <div className="report-card report-card-wide">
              <div className="report-card-title">Runs by workflow</div>
              <div className="hbar-list">
                {byWorkflow.map(w => (
                  <HBar key={w.name} label={w.name} value={w.runs} max={maxWorkflow} color="#818cf8"
                    sub={w.failed > 0 ? `${w.failed} failed` : 'all healthy'} />
                ))}
              </div>
            </div>

            {/* Recent activity */}
            <div className="report-card">
              <div className="report-card-title">Recent activity</div>
              <div className="activity-feed">
                {recent.map((e, i) => (
                  <div key={i} className={`activity-row activity-${e.type}`}>
                    <span className="activity-dot" />
                    <div className="activity-body">
                      <span className="activity-agent">{e.label}</span>
                      <span className="activity-action">{e.detail}</span>
                    </div>
                    <span className="activity-time">{e.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
